/**
 * BookingPaymentDialog
 *
 * Confirmation + method picker for the unified booking settlement. Shows the
 * selected lines and total (amount is fixed = selected total), the payment
 * method, and an optional external payer. Replaces FolioPaymentDialog +
 * SettleGuestChargeDialog with a single flow.
 *
 * BookingLayer-aware:
 *  - `warning` notice: paying local lines on a BL booking whose outbound sync
 *    isn't pushing live — the payment records locally but won't reach BL.
 *  - `info` notice: there's only BL-side debt (no local lines) — nothing to
 *    charge from Shaka; the operator is pointed to BookingLayer instead.
 * Either way the dialog still opens and offers a link to the BL booking.
 */
import { useState } from 'react';
import { Banknote, CreditCard, ArrowLeftRight, Wifi, MoreHorizontal, AlertTriangle, Info } from 'lucide-react';
import { useTranslation } from '@/core';
import { useLocationCurrency } from '@/shared/hooks/useLocationCurrency';
import { ExternalPlatformLink } from '@/shared/components/ExternalPlatformLink';
import { Button } from '@/theme/components/ui/button';
import { Input } from '@/theme/components/ui/input';
import { Label } from '@/theme/components/ui/label';
import { Textarea } from '@/theme/components/ui/textarea';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/theme/components/ui/dialog';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/theme/components/ui/collapsible';
import { ScrollArea } from '@/theme/components/ui/scroll-area';
import { cn } from '@/lib/utils';
import type { FolioPaymentMethod } from '../../types';

export interface BookingPaymentSubmit {
  method: FolioPaymentMethod;
  paidByName?: string;
  paidByNotes?: string;
}

interface SelectedLine {
  id: string;
  description: string;
  amount: number;
}

interface BookingPaymentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedLines: SelectedLine[];
  total: number;
  onSubmit: (input: BookingPaymentSubmit) => void;
  isLoading?: boolean;
  /**
   * BookingLayer notice for an integrated booking:
   *  - `warning`: outbound sync isn't pushing live → a local payment won't reach
   *    BL automatically, register it there.
   *  - `info`: there's only BL-side debt (no local lines) → settled in BL, open
   *    the booking there.
   * `url` deep-links the booking in BookingLayer (opens in a new tab).
   */
  blNotice?: { url: string; tone: 'warning' | 'info' } | null;
}

const METHOD_OPTIONS: { value: FolioPaymentMethod; icon: React.ComponentType<{ className?: string }> }[] = [
  { value: 'card', icon: CreditCard },
  { value: 'cash', icon: Banknote },
  { value: 'transfer', icon: ArrowLeftRight },
  { value: 'guest_prepaid', icon: Wifi },
  { value: 'other', icon: MoreHorizontal },
];

export function BookingPaymentDialog({
  open, onOpenChange, selectedLines, total, onSubmit, isLoading = false, blNotice = null,
}: BookingPaymentDialogProps) {
  const { t } = useTranslation();
  const { formatPrice } = useLocationCurrency();

  const [method, setMethod] = useState<FolioPaymentMethod>('card');
  const [paidByName, setPaidByName] = useState('');
  const [paidByNotes, setPaidByNotes] = useState('');
  const [extraOpen, setExtraOpen] = useState(false);

  // Nothing chargeable from Shaka (e.g. BL-only debt): the dialog still opens
  // but as an info surface, with no payment controls.
  const chargeable = selectedLines.length > 0 && total > 0;
  // Only surface BookingLayer-branded copy when this is genuinely a BookingLayer
  // booking (blNotice present). A Shaka-native/manual booking with nothing to
  // charge must NOT be mislabelled as "held in BookingLayer".
  const isBlContext = !!blNotice;

  const reset = () => {
    setMethod('card');
    setPaidByName('');
    setPaidByNotes('');
    setExtraOpen(false);
  };

  const handleOpenChange = (v: boolean) => {
    if (isLoading) return;
    if (!v) reset();
    onOpenChange(v);
  };

  const handleSubmit = () => {
    if (total <= 0) return;
    onSubmit({
      method,
      paidByName: paidByName.trim() || undefined,
      paidByNotes: paidByNotes.trim() || undefined,
    });
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>
            {chargeable
              ? t('bookingPayment.title', 'Record payment')
              : isBlContext
                ? t('bookingPayment.blOnlyHeader', 'BookingLayer balance')
                : t('bookingPayment.nothingHeader', 'Nothing to charge')}
          </DialogTitle>
          <DialogDescription>
            {chargeable ? (
              <>
                {t('bookingPayment.linesSelected', '{{count}} line(s) selected', { count: selectedLines.length })}
                {' · '}
                <span className="font-semibold">{formatPrice(total)}</span>
              </>
            ) : isBlContext ? (
              t('bookingPayment.blOnlyDesc', 'This balance is held in BookingLayer.')
            ) : (
              t('bookingPayment.nothingDesc', 'There are no open charges to collect for this booking.')
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* BookingLayer notice — warning (won't sync) or info (BL-only debt) */}
          {blNotice && (
            <div
              className={cn(
                'space-y-1.5 rounded-md border p-3',
                blNotice.tone === 'warning' ? 'border-warning/40 bg-warning/10' : 'border-border bg-muted/40',
              )}
            >
              <div className="flex items-start gap-2">
                {blNotice.tone === 'warning' ? (
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-warning" />
                ) : (
                  <Info className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                )}
                <div className="space-y-0.5">
                  <p className="text-xs font-semibold text-foreground">
                    {blNotice.tone === 'warning'
                      ? t('bookingPayment.blOutboundOffTitle', "Won't sync to BookingLayer")
                      : t('bookingPayment.blManagedTitle', 'Managed in BookingLayer')}
                  </p>
                  <p className="text-[11px] text-muted-foreground">
                    {blNotice.tone === 'warning'
                      ? t(
                          'bookingPayment.blOutboundOffDesc',
                          "Outbound sync is off, so this payment won't be sent to BookingLayer automatically. Register it in BookingLayer to keep the balance in sync.",
                        )
                      : t(
                          'bookingPayment.blManagedDesc',
                          'This outstanding balance is settled in BookingLayer, not charged from Shaka. Open the booking there to view or register the payment.',
                        )}
                  </p>
                </div>
              </div>
              <div className="pl-6">
                <ExternalPlatformLink
                  platform="BookingLayer"
                  url={blNotice.url}
                  label={t('bookingPayment.openInBl', 'Open in BookingLayer')}
                  className={cn(
                    'inline-flex items-center gap-1 text-xs font-medium hover:underline',
                    blNotice.tone === 'warning' ? 'text-warning' : 'text-primary',
                  )}
                />
              </div>
            </div>
          )}

          {chargeable && (
            <>
              {/* Selected lines */}
              <ScrollArea className="max-h-32 rounded-md border">
                <div className="space-y-1 p-2">
                  {selectedLines.map((line) => (
                    <div key={line.id} className="flex justify-between gap-2 text-xs">
                      <span className="truncate text-foreground">{line.description}</span>
                      <span className="font-mono tabular-nums shrink-0">{formatPrice(line.amount)}</span>
                    </div>
                  ))}
                </div>
              </ScrollArea>

              {/* Method */}
              <div className="space-y-1.5">
                <Label>{t('common.paymentMethod')}</Label>
                <div className="grid grid-cols-5 gap-1">
                  {METHOD_OPTIONS.map(({ value, icon: Icon }) => (
                    <button
                      key={value}
                      type="button"
                      onClick={() => setMethod(value)}
                      disabled={isLoading}
                      className={cn(
                        'flex flex-col items-center gap-1 rounded-md border p-2 text-xs transition-colors',
                        method === value ? 'border-primary bg-primary/10 text-primary' : 'border-border hover:bg-muted',
                      )}
                    >
                      <Icon className="h-4 w-4" />
                      {t(`folio.method.${value}`)}
                    </button>
                  ))}
                </div>
              </div>

              {/* External payer (collapsible) */}
              <Collapsible open={extraOpen} onOpenChange={setExtraOpen}>
                <CollapsibleTrigger asChild>
                  <Button variant="ghost" size="sm" className="w-full justify-start gap-1 text-xs text-muted-foreground">
                    {extraOpen ? '−' : '+'} {t('folio.externalPayer')}
                  </Button>
                </CollapsibleTrigger>
                <CollapsibleContent className="space-y-3 pt-2">
                  <div className="space-y-1.5">
                    <Label htmlFor="bk-paid-by-name" className="text-xs">{t('folio.paidByName')}</Label>
                    <Input
                      id="bk-paid-by-name"
                      placeholder={t('folio.paidByNamePlaceholder')}
                      value={paidByName}
                      onChange={(e) => setPaidByName(e.target.value)}
                      disabled={isLoading}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="bk-paid-by-notes" className="text-xs">{t('folio.paidByNotes')}</Label>
                    <Textarea
                      id="bk-paid-by-notes"
                      placeholder={t('folio.paidByNotesPlaceholder')}
                      value={paidByNotes}
                      onChange={(e) => setPaidByNotes(e.target.value)}
                      disabled={isLoading}
                      rows={2}
                      className="resize-none text-sm"
                    />
                  </div>
                </CollapsibleContent>
              </Collapsible>
            </>
          )}
        </div>

        <DialogFooter>
          {chargeable ? (
            <>
              <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={isLoading}>
                {t('common.cancel')}
              </Button>
              <Button onClick={handleSubmit} disabled={total <= 0 || isLoading}>
                {isLoading ? t('common.processing') : `${t('bookingPayment.charge', 'Charge')} ${formatPrice(total)}`}
              </Button>
            </>
          ) : (
            <Button variant="outline" onClick={() => handleOpenChange(false)}>
              {t('common.close', 'Close')}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
